import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Target, Heart, Users, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/ResourceCard";
import { IMG, COMPLIANCE_TEXT } from "@/lib/assets";
import { FounderTeaser } from "@/pages/Founder";

const VALUES = [
  { icon: ShieldCheck, title: "Verified & Transparent", desc: "Every listing is verified before publication and clearly attributed to its provider." },
  { icon: Target, title: "Opportunity-Focused", desc: "We connect people to real, legitimate pathways — education, housing, and assistance." },
  { icon: Heart, title: "Community First", desc: "Programs are designed around the real needs of individuals, youth, and families." },
];

export default function About() {
  return (
    <main>
      <PageHero overline="About the Foundation" image={IMG.youth1}
        title="A charitable platform for real opportunity"
        subtitle="Millionaire Mindsetters Foundation is a registered 501(c)(3) public charity helping communities discover education, assistance, and meaningful opportunities." />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-20 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHeading overline="Our Story" title="Built like a national foundation, rooted in community"
            subtitle="Founded in 2021, we are an early-stage foundation building a modern, technology-enabled platform that combines charitable programs, community education, and resource discovery. Our Opportunity Marketplace brings verified third-party programs together so our community can find what they need — without confusion, funnels, or hidden agendas. As our programs grow, we will publish audited impact results here." />
          <div className="grid grid-cols-3 gap-6 mt-8">
            {[["2021", "Founded"], ["501(c)(3)", "Public Charity"], ["Verified", "Every Listing"]].map(([v, l], i) => (
              <div key={i}>
                <div className="font-display text-3xl font-bold text-navy-900">{v}</div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <img src={IMG.youth2} alt="Community education" className="rounded-3xl w-full h-[460px] object-cover shadow-2xl" />
      </section>

      <FounderTeaser dark />

      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeading overline="Our Values" title="How we operate" center />
          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                <div className="h-16 w-16 rounded-2xl bg-navy-900 text-gold-light flex items-center justify-center mx-auto mb-5"><v.icon className="h-8 w-8" /></div>
                <h3 className="font-display text-xl font-bold text-navy-900">{v.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-20">
        <div className="bg-navy-900 rounded-3xl p-8 sm:p-10 text-white">
          <h3 className="font-display text-2xl font-bold mb-4 flex items-center gap-3"><ShieldCheck className="h-7 w-7 text-verified" /> Compliance & Separation</h3>
          <p className="text-slate-300 leading-relaxed">{COMPLIANCE_TEXT}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
          <Link to="/donate"><Button size="lg" className="rounded-full bg-gold hover:bg-gold-light text-white px-8 h-13 h-14">Support Our Mission</Button></Link>
          <Link to="/sponsor"><Button size="lg" variant="outline" className="rounded-full px-8 h-14"><Handshake className="h-5 w-5 mr-2" /> Corporate Sponsorship</Button></Link>
        </div>
      </section>
    </main>
  );
}
